import { useEffect, useState } from 'react';
import { Modal, Spinner } from 'react-bootstrap';
import { getLlmPromptHistory, getLlmPromptVersion, restoreLlmPromptVersion } from '../../api/api';
import { assembleRows } from './promptText';

const fmtDate = (d) => new Date(d).toLocaleString('fi-FI', { dateStyle: 'short', timeStyle: 'short' });

export default function PromptHistory({ promptKey, scope, userId, currentVersionId, canRestore, disabled, onRestored }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState(null);
  const [error, setError] = useState(null);
  const [confirmId, setConfirmId] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [viewing, setViewing] = useState(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setItems(null);
    setError(null);
    getLlmPromptHistory(promptKey, scope, userId)
      .then(res => { if (!cancelled) setItems(res.data); })
      .catch(() => { if (!cancelled) setError('Historian lataus epäonnistui.'); });
    return () => { cancelled = true; };
  }, [open, promptKey, scope, userId, currentVersionId]);

  const view = async (item) => {
    setViewing({ item, rows: null });
    try {
      const res = await getLlmPromptVersion(item.id);
      setViewing({ item, rows: res.data.rows });
    } catch {
      setViewing({ item, rows: [], error: 'Version lataus epäonnistui.' });
    }
  };

  const restore = async (item) => {
    setBusyId(item.id);
    try {
      await restoreLlmPromptVersion(item.id);
      setConfirmId(null);
      onRestored?.(item);
    } catch (err) {
      setError(err?.response?.data?.error || 'Palautus epäonnistui.');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="border rounded bg-white">
      <button
        type="button"
        className="btn btn-sm w-100 text-start d-flex align-items-center gap-2 px-2 py-2"
        onClick={() => setOpen(o => !o)}
      >
        <i className={`bi bi-chevron-${open ? 'down' : 'right'}`} />
        <strong style={{ fontSize: '0.9rem' }}>Versiohistoria</strong>
      </button>

      {open && (
        <div className="px-2 pb-2">
          {error && <div className="alert alert-danger py-1 px-2 mb-2" style={{ fontSize: '0.8rem' }}>{error}</div>}
          {!items && !error && <Spinner animation="border" size="sm" />}
          {items && items.length === 0 && <div className="text-muted" style={{ fontSize: '0.8rem' }}>Ei versioita.</div>}
          {items && items.map((item) => {
            const isCurrent = item.id === currentVersionId;
            return (
              <div key={item.id} className="d-flex align-items-center gap-2 border-top py-2 flex-wrap" style={{ fontSize: '0.8rem' }}>
                <span className="badge bg-secondary">v{item.version_no}</span>
                {isCurrent && <span className="badge bg-success">käytössä</span>}
                <span className="text-muted">{fmtDate(item.created_at)}</span>
                <span>{item.creator_name ?? `käyttäjä ${item.created_by}`}</span>
                <span className="text-muted">· {item.row_count} riviä</span>
                {item.note && <span className="fst-italic">“{item.note}”</span>}
                <div className="ms-auto d-flex gap-1">
                  <button type="button" className="btn btn-sm btn-outline-secondary py-0" onClick={() => view(item)}>Näytä</button>
                  {canRestore && !isCurrent && (
                    confirmId === item.id ? (
                      <>
                        <button type="button" className="btn btn-sm btn-warning py-0" disabled={disabled || busyId === item.id} onClick={() => restore(item)}>
                          {busyId === item.id ? 'Palautetaan…' : 'Vahvista palautus'}
                        </button>
                        <button type="button" className="btn btn-sm btn-link py-0" onClick={() => setConfirmId(null)}>Peru</button>
                      </>
                    ) : (
                      <button type="button" className="btn btn-sm btn-outline-primary py-0" disabled={disabled} title={disabled ? 'Tallenna tai peru muutokset ensin' : ''} onClick={() => setConfirmId(item.id)}>
                        Palauta
                      </button>
                    )
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Modal show={!!viewing} onHide={() => setViewing(null)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: '1rem' }}>
            Versio v{viewing?.item.version_no} · {viewing && fmtDate(viewing.item.created_at)}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {viewing?.error && <div className="alert alert-danger">{viewing.error}</div>}
          {viewing && !viewing.rows && <Spinner animation="border" size="sm" />}
          {viewing?.rows && (
            <pre style={{ whiteSpace: 'pre-wrap', fontSize: '0.8rem', margin: 0 }}>{assembleRows(viewing.rows)}</pre>
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
}
